import React, { useCallback } from 'react'

import { arrayOf, number, shape, string } from 'prop-types'

import usePageView from 'Pods/Shared/Hooks/usePageView'
import I18n from 'Pods/Shared/I18n'

import SubscriptionTerms from '../Components/SubscriptionTerms/SubscriptionTerms'
import useCheckoutTracking from '../Hooks/useCheckoutTracking'
import OrderSummary from './Components/OrderSummary/OrderSummary'
import PaymentMethod from './Components/PaymentMethod/PaymentMethod'
import useUpgradeCheckout from './Hooks/useUpgradeCheckout'

const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'
const DEFAULT_COMMIT_URL = '/profile/subscription/upgrade'

function Upgrade({
  premiumPrice,
  downloadsUsed,
  unusedCredits,
  discount = 0,
  amountDueNow = 0,
  vatAmount = null,
  premiumPlan = null,
  paymentMethod = null,
  updatePaymentData = null,
  subscriptionTier = 'essentials',
  returnPath = DEFAULT_RETURN_PATH,
  commitUrl = DEFAULT_COMMIT_URL,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const tracking = useCheckoutTracking({ isUpgradeMode: true, tier: subscriptionTier })

  usePageView(tracking.trackView)

  const {
    useSavedMethod,
    isDifferentOpen,
    isProcessing,
    isPayDisabled,
    handleSelectSaved,
    handleToggleDifferent,
    handleFormReady,
    handleFormValidityChange,
    handlePaymentProcessing,
    handlePaymentSuccess,
    handlePay,
  } = useUpgradeCheckout({ paymentMethod, commitUrl, returnPath, tracking })

  return (
    <div className="subscription-checkout subscription-upgrade">
      <div className="subscription-checkout__main">
        <div className="subscription-checkout__content">
          {isProcessing ? (
            <p className="upgrade-processing cgt-body-base">
              {translate('subscription_checkout.payment_success_processing')}
            </p>
          ) : (
            <PaymentMethod
              paymentMethod={paymentMethod}
              updatePaymentData={updatePaymentData}
              useSavedMethod={useSavedMethod}
              isDifferentOpen={isDifferentOpen}
              onSelectSaved={handleSelectSaved}
              onToggleDifferent={handleToggleDifferent}
              onFormReady={handleFormReady}
              onFormValidityChange={handleFormValidityChange}
              onPaymentProcessing={handlePaymentProcessing}
              onPaymentSuccess={handlePaymentSuccess}
              tracking={tracking}
              locale={locale}
            />
          )}
        </div>
      </div>

      <div className="subscription-checkout__order-details">
        <OrderSummary
          premiumPrice={premiumPrice}
          discount={discount}
          downloadsUsed={downloadsUsed}
          unusedCredits={unusedCredits}
          amountDueNow={amountDueNow}
          vatAmount={vatAmount}
          premiumPlan={premiumPlan}
          onPay={handlePay}
          disabled={isPayDisabled}
          locale={locale}
        />
        <SubscriptionTerms
          monthlyPrice={premiumPrice}
          locale={locale}
          planKey="premium"
        />
      </div>
    </div>
  )
}

Upgrade.propTypes = {
  premiumPrice: number.isRequired,
  downloadsUsed: number.isRequired,
  unusedCredits: number.isRequired,
  discount: number,
  amountDueNow: number,
  vatAmount: number,
  premiumPlan: shape({
    features: arrayOf(string),
    originalPrice: number,
    currentPrice: number,
    priceUnit: string,
  }),
  paymentMethod: string,
  updatePaymentData: shape({
    merchant: string,
    signature: string,
    paymentIntent: string,
    returnPath: string,
  }),
  subscriptionTier: string,
  returnPath: string,
  commitUrl: string,
  locale: string,
}

export default Upgrade
